import moment from 'moment';
import { getCaseType } from 'Utils';
import { estimateOf } from 'Components/Professional/Cases/CaseCard';

// Firm outreach - email templates (UI only for now; sending is wired later).
// Only the same blurb a firm would see in Available Cases goes in the email:
// case type, estimate, where it happened and dates. Never client details.

export const TEMPLATES = {
  recommended: { key: 'recommended', label: 'Recommended for a case', short: 'Recommended' },
  saved: { key: 'saved', label: 'Saved by a client', short: 'Saved by client' },
};

const cap = (s) => (s ? String(s).replace(/(^|\s)\S/g, (c) => c.toUpperCase()) : '');
export const placeOf = (d) => [cap(d?.caseCity), d?.caseState].filter(Boolean).join(', ');

// one line per case, e.g. "Medical Malpractice · Dallas, Texas · Est. $800,000 – $2,000,000 · Posted Oct 7, 2026"
export function caseBlurb(d) {
  const parts = [getCaseType(d?.caseType) || 'Case'];
  const where = placeOf(d);
  if (where) parts.push(where);
  const est = estimateOf(d);
  if (est) parts.push('Est. ' + est);
  if (d?.created_at) parts.push('Posted ' + moment(d.created_at).format('MMM D, YYYY'));
  return parts.join(' · ');
}

// clicks: how many times clients clicked through to this firm's website from
// their matches. When > 0 the message gets one extra line the admin can edit or delete.
// account: the firm already has an account ({ state: 'verified' | 'review', premium })
// -> same email, but "Log in →" instead of "Claim your firm →" (no second claim).
export function buildEmail(template, firmName, cases, { clicks = 0, account = null } = {}) {
  const list = (cases || []).filter(Boolean);
  const first = list[0];
  const many = list.length > 1;
  const name = firmName || 'there';
  const lines = list.map((d) => '• ' + caseBlurb(d)).join('\n');
  const clickLine = clicks > 0
    ? `\n\n${clicks > 1 ? 'Clients' : 'A client'} on Quick Case Review also clicked through to your website from their firm matches.`
    : '';
  const these = many ? 'these cases' : 'this case';
  const them = many ? 'them' : 'it';
  const review = account?.state === 'review';
  // the closing lines for a firm that already has an account
  const loginClose = (saved) => review
    ? (saved
      ? `Log in to Quick Case Review to see ${them}. You can request to view more once your firm's profile is verified.`
      : `Log in to Quick Case Review to check your profile. Once it's verified, you can upgrade to Premium to see ${these}.`)
    : saved || account?.premium
      ? `Log in to Quick Case Review to see ${these} and ${saved ? 'respond' : `request to review ${them}`}.`
      : `Log in to Quick Case Review and upgrade to Premium to see ${these} and request to review ${them}.`;
  const close = (saved, claimText) => (account
    ? `${loginClose(saved)}

Log in →`
    : `${claimText}

Claim your firm →`);

  if (template === 'saved') {
    return {
      subject: many
        ? `Clients saved ${firmName || 'your firm'} on Quick Case Review`
        : `A client saved ${firmName || 'your firm'} for a ${getCaseType(first?.caseType) || 'legal'} case`,
      body:
`Hello ${name} team,

${many ? `Clients reviewing their legal options on Quick Case Review saved your firm as one they would like to hear from:` : `A client reviewing their legal options on Quick Case Review saved your firm as one they would like to hear from:`}

${lines}${clickLine}

${close(true, `Quick Case Review is where clients in Texas post their cases and law firms request to review them. Claim your firm's free profile to see ${these} and respond.`)}

The Quick Case Review team`,
    };
  }
  return {
    subject: many
      ? `${firmName || 'Your firm'} was recommended for ${list.length} cases on Quick Case Review`
      : `${firmName || 'Your firm'} was recommended for a ${getCaseType(first?.caseType) || 'legal'} case${placeOf(first) ? ' in ' + cap(first?.caseCity || '') : ''}`,
    body:
`Hello ${name} team,

${many ? `Your firm was recommended to clients on Quick Case Review for ${list.length} cases that fit your practice:` : `Your firm was recommended to a client on Quick Case Review for a case that fits your practice:`}

${lines}${clickLine}

${close(false, `Quick Case Review is where clients in Texas post their cases and law firms request to review them. Claim your firm's free profile to see ${these} and others like ${them}.`)}

The Quick Case Review team`,
  };
}

export const isEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(String(v || '').trim());

// Firms recommended on a case (stored on the case by the AI firm match)
export function firmsOfCase(d) {
  const tag = (list, group) => (Array.isArray(list) ? list : []).filter((f) => f && f.name).map((f) => ({ ...f, group }));
  return [...tag(d?.aiLocalFirms, 'Larger firm'), ...tag(d?.aiSmallFirms, 'Smaller firm')];
}

// Stable key for a firm across cases: the synced Firm id, else its cleaned-up website/name
export const firmKey = (f) =>
  String(f?.firmid || f?._id || (f?.website || '').toLowerCase().replace(/^https?:\/\//, '').replace(/^www\./, '').replace(/\/$/, '') || (f?.name || '').toLowerCase().trim());

export const withScheme = (v) => (/^https?:\/\//i.test(String(v)) ? String(v) : 'https://' + v);
